import { useState } from "react";
import { Check, Compass, Loader2, Sparkles, TriangleAlert, X } from "lucide-react";
import { fetchRecommendations } from "../lib/api";
import { dictionaries } from "../lib/i18n";
import { TripFromText } from "./TripFromText";
import type { Interest, TripForm, UiLang, Recommendation } from "../lib/types";

interface Props {
  lang: UiLang;
  /** Dagene brukeren allerede har valgt — lengden teller med i vurderingen. */
  days: number;
  /**
   * Hvilke måneder hver himmelinteresse er mulig i, fra serveren.
   *
   * Regnet ut av den samme koden som gir poengsummen, framfor skrevet ned her.
   * En månedsliste i grensesnittet ville blitt en andre sannhet som sluttet
   * å stemme i det noen rørte astronomien — og da ville knappen vært skjult
   * mens rangeringen fortsatt belønnet den. Mangler den, sperres ingenting.
   */
  seasons?: Partial<Record<Interest, number[]>>;
  /**
   * Turens egen dato, når den er satt.
   *
   * Panelet hadde sin egen måned fra systemklokka, som datoen i
   * kontrollpanelet aldri nådde. Da ville en sesongsperre sperret på feil
   * måned hver gang brukeren faktisk hadde svart på «når reiser du?».
   */
  startDate?: string;
  onPick: (corridorId: string) => void;
  /**
   * Hele skjemaet, fylt ut av språkmodellen fra en fritekstbeskrivelse.
   *
   * Ligger her og ikke i sitt eget kort fordi det er samme spørsmål: den som
   * ikke vet hvor de skal, skal møte ett sted å spørre, ikke to. Modellen er
   * den raske veien; interessevalget under er den som alltid virker.
   */
  onDescribed?: (form: TripForm) => void;
}

/**
 * Hjelper den som ikke vet hvor i Norge de vil.
 *
 * Turister kjenner sjelden forskjellen på Hardangervidda og Lofoten, men de
 * vet at de vil se fjorder, gå i fjellet eller oppleve nordlys. Panelet
 * oversetter det til en rute, og viser hvorfor — hver anbefaling kommer med
 * begrunnelsen sin, så forslaget kan overprøves i stedet for å bare adlydes.
 *
 * Måneden spørres om fordi svaret avhenger av den: nordlys finnes ikke i juni,
 * midnattssol ikke i november, og flere fjelloverganger er stengt om vinteren.
 */

const INTERESTS: Interest[] = [
  "fjord",
  "fjell",
  "by",
  "historie",
  "mat",
  "nordlys",
  "midnattssol",
  "strand",
  "dyreliv",
  "tog",
  "ski",
];

export function RouteFinder({ lang, days, seasons, startDate, onPick, onDescribed }: Props) {
  const t = dictionaries[lang];
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<Interest[]>([]);
  /*
   * Måneden, fra turens egen dato når den er satt.
   *
   * Panelet hadde sin egen måned, sådd fra systemklokka, som datoen i
   * kontrollpanelet aldri nådde. Setter man turen til 20. desember og åpner
   * finneren i september, ville en sesongsperre sperret på *september* — altså
   * vært trygt feil framfor fraværende.
   */
  const [month, setMonth] = useState(() =>
    startDate ? Number(startDate.slice(5, 7)) : new Date().getMonth() + 1,
  );
  /*
   * Hvorfor en knapp ikke teller med, når noen trykker på den.
   *
   * Ikke `disabled`: en deaktivert knapp gir ingen trykkhendelse på telefon, så
   * det finnes ingen bevegelse igjen som kunne avslørt forklaringen. Den blir
   * et grått rektangel som ignorerer tommelen og aldri sier hvorfor.
   */
  const [explained, setExplained] = useState<Interest | null>(null);
  const [results, setResults] = useState<Recommendation[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * Om måneden gjør interessen fysisk umulig.
   *
   * Svaret kommer fra serveren, som regner det ut av den samme koden som gir
   * poengsummen — ikke fra en månedsliste her. En liste i grensesnittet ville
   * blitt en andre sannhet som sluttet å stemme i det noen rørte astronomien,
   * og da ville knappen vært skjult mens rangeringen fortsatt belønnet den.
   *
   * Interesser serveren ikke har en mening om, er alltid mulige. Det er med
   * vilje: ski, dyreliv, strand og fjell *ser* sesongbetonte ut og er det
   * ikke, og en port på dem ville slettet sommerskisentrene og
   * spekkhoggersesongen.
   */
  const offSeason = (interest: Interest): boolean => {
    const months = seasons?.[interest];
    return months !== undefined && !months.includes(month);
  };

  /*
   * Valgte interesser måneden har avlyst.
   *
   * De blir stående valgt. Å fjerne et valg brukeren har gjort er en endring
   * de ikke ba om, og på telefon ligger knappen som forsvinner sannsynligvis
   * under skjermkanten fra månedsvelgeren de nettopp trykket på — så de ville
   * aldri sett det skje. Sagt, ikke filtrert stille.
   */
  const chosenOff = chosen.filter(offSeason);

  const toggle = (interest: Interest) => {
    setChosen((current) =>
      current.includes(interest)
        ? current.filter((value) => value !== interest)
        : [...current, interest],
    );
    // Gamle forslag hører til gamle valg.
    setResults(null);
  };

  const search = async () => {
    if (chosen.length === 0) {
      setError(t.recommendEmpty);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const { recommendations } = await fetchRecommendations(chosen, days, month, lang);
      setResults(recommendations);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : t.genericError);
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <button type="button" className="finder-open" onClick={() => setOpen(true)}>
        <Compass size={15} />
        <span>
          <strong>{t.helpMeChoose}</strong>
          <small>{t.helpMeChooseHint}</small>
        </span>
      </button>
    );
  }

  return (
    <div className="finder">
      <button
        type="button"
        className="finder-close"
        onClick={() => setOpen(false)}
        aria-label={t.recommendClose}
      >
        <X size={16} />
      </button>

      <div className="finder-head">
        <Compass size={15} />
        {t.helpMeChoose}
      </div>

      {/*
        Den raske veien først. Feiler modellen — tom kvote, nede, uforståelig
        tekst — står interessevalget rett under og gjør nøyaktig samme jobb
        uten en eneste modell. Det er derfor de to hører hjemme i samme panel:
        den ene er reserveløsningen for den andre, og det er lettere å se når
        de står ved siden av hverandre.
      */}
      {onDescribed && (
        <>
          <TripFromText
            lang={lang}
            embedded
            onFilled={(form) => {
              onDescribed(form);
              setOpen(false);
            }}
          />
          <div className="finder-or">
            <span>{t.aiOrPickInterests}</span>
          </div>
        </>
      )}

      {/*
        Måneden først, over knappene.

        Den sto under dem, så brukeren bandt seg til interessene før appen visste
        hvilken måned det gjaldt — og en sesongsperre som svarer etterpå er en
        sperre som flytter knapper mens fingeren er på vei.
      */}
      <div className="field">
        <label className="field-label" htmlFor="finder-month">
          {t.monthLabel}
          {/*
            Egen tekst, ikke panelets ingress om igjen. Her sto
            `helpMeChooseHint` — «si hva du har lyst til å oppleve» — under
            «Når reiser du?», altså et svar på et annet spørsmål.
          */}
          <span className="field-hint">{t.monthHint}</span>
        </label>
        <select
          id="finder-month"
          className="date-input"
          value={month}
          onChange={(event) => {
            setMonth(Number(event.target.value));
            setResults(null);
            setExplained(null);
          }}
        >
          {t.monthName.map((name, index) => (
            <option key={name} value={index + 1}>
              {name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <span className="field-label">{t.interestsLabel}</span>
        <div className="chips" role="group" aria-label={t.interestsLabel}>
          {INTERESTS.map((interest) => {
            const active = chosen.includes(interest);
            const off = offSeason(interest);
            return (
              <button
                key={interest}
                type="button"
                className={`chip ${active ? "chip--on" : ""} ${off ? "chip--off" : ""}`}
                aria-pressed={active}
                /*
                  `aria-disabled`, ikke `disabled`. En deaktivert knapp gir ingen
                  trykkhendelse på telefon, så det finnes ingen bevegelse igjen
                  som kunne avslørt hvorfor — og et `title` er usynlig på berøring.
                  En knapp som svarer når man trykker, er den eneste utgaven av
                  «ikke mulig» som virker på en skjerm man tar på.
                */
                aria-disabled={off || undefined}
                onClick={() => (off ? setExplained(interest) : toggle(interest))}
              >
                {active && <Check size={13} strokeWidth={3} />}
                {t.interestName[interest]}
                {/* Synlig tekst, ikke et `title`. Se over. */}
                {off && <small>{t.interestOffSeason(t.monthName[month - 1])}</small>}
              </button>
            );
          })}
        </div>
        {explained && (
          <p className="note">
            {t.interestOffSeasonWhy(t.interestName[explained], t.monthName[month - 1])}
          </p>
        )}
      </div>

      {/*
        Valgt, men avlyst av måneden.

        Valget blir stående — å fjerne det ville vært en endring brukeren ikke
        ba om — men rangeringen teller det ikke med, og det skal stå her framfor
        å oppdages som en prosent som ikke stemmer. Samme regel som de
        vinterstengte stoppene i itinerary.ts: sagt, ikke filtrert stille.
      */}
      {chosenOff.length > 0 && (
        <p className="note note--warn finder-off">
          <span>
            {t.interestOffSeasonChosen(
              chosenOff.map((interest) => t.interestName[interest]).join(", "),
              t.monthName[month - 1],
            )}
          </span>
          <button
            type="button"
            className="linkish"
            onClick={() => setChosen((current) => current.filter((i) => !offSeason(i)))}
          >
            {t.interestOffSeasonRemove}
          </button>
        </p>
      )}

      <button type="button" className="generate" onClick={search} disabled={loading}>
        {loading ? (
          <>
            <Loader2 size={17} className="spin" /> {t.finding}
          </>
        ) : (
          t.findRoutes
        )}
      </button>

      {error && <p className="note note--warn">{error}</p>}

      {results && results.length > 0 && (
        <div className="finder-results">
          <h3 className="drawer-section-title">{t.recommendTitle}</h3>
          {results.slice(0, 3).map((result, index) => (
            <div className={`suggestion ${index === 0 ? "suggestion--top" : ""}`} key={result.corridorId}>
              <div className="suggestion-head">
                <strong>{result.name}</strong>
                <span className="suggestion-score">
                  {index === 0 && <Sparkles size={11} />}
                  {t.recommendMatch(result.score)}
                </span>
              </div>

              <p className="suggestion-tagline">{result.tagline}</p>

              <ul className="suggestion-reasons">
                {result.reasons.map((reason) => (
                  <li key={reason}>{reason}</li>
                ))}
              </ul>

              {result.caution && (
                <p className="suggestion-caution">
                  <TriangleAlert size={12} />
                  {result.caution}
                </p>
              )}

              <button
                type="button"
                className="suggestion-pick"
                onClick={() => {
                  onPick(result.corridorId);
                  setOpen(false);
                }}
              >
                {t.recommendPick}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
